<template>
    <div class="bg-white py-6 md:py-10 border-0 md:border border-border rounded-lgr px-4 md:px-6 flex flex-col gap-6 md:gap-10 fs-mask">
        <div class="flex flex-col">
            <ProfileNameEditor :user-name="userName" :first-name="firstName" :last-name="lastName" :endpoints="endpoints" class="min-h-7 md:min-h-8" />
            <div v-if="yearsOfExperience || jobTitle" class="text-ink-muted flex gap-4 md:gap-6 min-h-7 md:min-h-8">
                <ProfileTag v-if="yearsOfExperience" :model-value="yearsOfExperience" icon="bi-graph-up-arrow" />
                <ProfileEditableField v-if="jobTitle" field="job_title" :value="jobTitle" :endpoints="endpoints" />
            </div>
            <div class="mt-3">
                <div class="flex items-center gap-2 flex-wrap">
                    <p class="body text-ink-muted shrink-0">{{ t("profile.header.strengthLabel") }}</p>
                    <BaseProgressBar :value="completionPercent" :aria-label="strengthA11yLabel" show-label class="w-48" />
                </div>
                <p v-if="!isComplete" class="body-small text-ink-muted">{{ t("profile.header.strengthHint") }}</p>
            </div>
        </div>
        <HorizontalDivider class="my-0!" />
        <div class="flex flex-col items-center gap-3 md:gap-4 text-center">
            <h2 class="display-serif">{{ t("profile.header.journeyTitle") }}</h2>
            <p class="body-small text-ink-muted max-w-91.5">{{ t("profile.header.journeyDescription") }}</p>
            <BaseProgressBar class="w-3/4 relative top-9.5 z-0" :value="barPercent" size="xs" :aria-label="t('profile.header.journeyProgressA11yLabel')" />
            <div class="grid grid-cols-4 w-full items-start justify-center">
                <!-- progress step circle -->
                <div v-for="step in progressSteps" :key="step.id" class="flex flex-col items-center">
                    <ProgressCircle
                        class="min-w-10 max-w-10 min-h-10 relative mb-4"
                        aria-hidden="true"
                        :class="[currentProgressStep.id === step.id && 'scale-125 shadow-md']"
                        content=""
                        :progress="currentProgressStep.id > step.id ? 100 : currentProgressStep.id === step.id ? (completionPercent % 33) * 3 : 0"
                        gradient-from="var(--color-progress-fill)"
                        gradient-to="var(--color-progress-track)">
                        <i class="bi bi-stars text-base/none absolute inset-x-0 top-3" aria-hidden="true"></i>
                    </ProgressCircle>
                    <p :class="[currentProgressStep.id === step.id ? 'body-small-bold md:body-bold' : 'body-small md:body']">{{ step.label }}</p>
                    <p v-if="currentProgressStep.id === step.id" class="details text-ink-muted hidden lg:inline">{{ step.subtitle }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import ProgressCircle from "~vue/components/ProgressCircle.vue";
import ProfileEditableField from "~vue/UserProfile/ProfileEditableField.vue";
import ProfileNameEditor from "~vue/UserProfile/ProfileNameEditor.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import ProfileTag from "./ProfileTag.vue";
import type { UserProfileEndpoints } from "./types";

type ProfileHeaderProps = {
    userName: string;
    firstName: string;
    lastName: string;
    jobTitle: string | null;
    yearsOfExperience: string | null;
    completionPercent: number;
    endpoints: UserProfileEndpoints;
};

const { endpoints, completionPercent, firstName = "", lastName = "", jobTitle = "", yearsOfExperience = "" } = defineProps<ProfileHeaderProps>();

const { t } = useI18n();

const isComplete = computed(() => completionPercent === 100);

// One message holding the visible label, so the progress bar's accessible name always contains it (WCAG 2.5.3).
const strengthA11yLabel = computed(() =>
    t("profile.header.strengthA11yLabel", { label: t("profile.header.strengthLabel"), percent: formatNumber(completionPercent / 100, "percent") }),
);

const progressSteps = computed(() => [
    { id: 1, label: t("profile.header.steps.startingOut.label"), subtitle: t("profile.header.steps.startingOut.subtitle") },
    { id: 2, label: t("profile.header.steps.gettingToKnowYou.label"), subtitle: t("profile.header.steps.gettingToKnowYou.subtitle") },
    { id: 3, label: t("profile.header.steps.buildingPicture.label"), subtitle: t("profile.header.steps.buildingPicture.subtitle") },
    { id: 4, label: t("profile.header.steps.coachingWithInsight.label"), subtitle: t("profile.header.steps.coachingWithInsight.subtitle") },
]);

const currentProgressStep = computed(() => {
    if (completionPercent === 100) {
        return progressSteps.value[3];
    } else if (completionPercent >= 66) {
        return progressSteps.value[2];
    } else if (completionPercent >= 33) {
        return progressSteps.value[1];
    } else {
        return progressSteps.value[0];
    }
});

const barPercent = computed(() => {
    return (currentProgressStep.value.id - 1) * 33.33;
});
</script>

<style scoped></style>
